interface QueueItem<T, R> {
  task: T;
  resolve: (result: R) => void;
  reject: (error: unknown) => void;
}

type Worker<T, R> = (task: T) => Promise<R>;

export class WorkerQueue<T, R> {
  private queue: QueueItem<T, R>[];
  private readonly worker: Worker<T, R>;
  private readonly numOfWorkers: number;
  private runningWorkersCount: number;

  constructor(worker: Worker<T, R>, numOfWorkers = 1) {
    this.queue = [];
    this.worker = worker;
    this.numOfWorkers = numOfWorkers;
    this.runningWorkersCount = 0;
  }

  push(item: T) {
    const promise = new Promise<R>((resolve, reject) => {
      this.queue.push({ task: item, resolve, reject });
    });
    this.callWorkers();
    return promise;
  }

  private callWorkers() {
    const workersToStart = this.numOfWorkers - this.runningWorkersCount;
    for (let i = 0; i < workersToStart; i++) {
      this.callNextTask();
    }
  }

  private async callNextTask() {
    const item = this.queue.shift();
    if (!item) {
      return;
    }
    this.runningWorkersCount++;
    try {
      const result = await this.worker(item.task);
      item.resolve(result);
    } catch (e) {
      item.reject(e);
    }
    this.runningWorkersCount--;
    this.callWorkers();
  }
}
